import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import { Container } from 'theme-ui';
import PageComponent from 'components/layout/Page';
import PageHero from 'components/ui/PageHero';
import RichTextGraphQl from 'components/ui/RichTextGraphQl';
import { PageFieldsFragment as Page } from 'graphql/generated/sdk';
import sdk from 'graphql/sdk';
import getSingleValue from 'lib/get-single-value';

type PageProps = {
  page: Page;
};

export default function ContentfulPage({ page }: PageProps) {
  return (
    <>
      <NextSeo
        title={page.seoMetadata.title}
        description={page.seoMetadata.description}
        openGraph={{
          title: page.seoMetadata.ogTitle,
        }}
      />
      <PageComponent>
        <PageHero
          title={page.title}
          subtitle={page.subtitle}
          heroImageUrl={page.heroImage?.url}
          heroImageAlt={page.heroImage?.description}
        />
        <Container variant="slim">
          <RichTextGraphQl
            bodyJson={page.content.json}
            links={page.content.links}
          />
        </Container>
      </PageComponent>
    </>
  );
}

export const getStaticProps: GetStaticProps<PageProps> = async ({ params }) => {
  const slug = getSingleValue(params, 'slug');
  const pageData = await sdk.pages({ slug, limit: 1 });
  const page = pageData.pageCollection.items[0];
  if (!page) return { notFound: true };

  return {
    props: { page },
    revalidate: 60,
  };
};

export async function getStaticPaths() {
  const data = await sdk.pageSlugs();
  const pages = data.pageCollection.items;
  return {
    paths: pages.map(({ slug }) => `/${slug}`) ?? [],
    fallback: 'blocking',
  };
}
